import { missionCatalog, soldierMissionId } from '../../content/mapCatalog';
import type { Point } from '../types';

export interface MissionMarker extends Point {
  missionId: string;
}

export interface MapLayout {
  version: 1;
  markers: MissionMarker[];
}

const missionIds = new Set(missionCatalog.map((mission) => mission.id));

// Samme kontrol bruges ved læsning i browseren og før skrivning til projektet.
export function parseMapLayout(value: unknown): MapLayout {
  if (!value || typeof value !== 'object') throw new Error('Kortopsætningen mangler.');
  const layout = value as Partial<MapLayout>;
  if (layout.version !== 1 || !Array.isArray(layout.markers))
    throw new Error('Kortopsætningen har et ukendt format.');
  const seen = new Set<string>();
  const markers = layout.markers.map((value: unknown) => {
    if (!value || typeof value !== 'object') throw new Error('Ugyldig markør.');
    const marker = value as Partial<MissionMarker>;
    if (
      typeof marker.missionId !== 'string' ||
      !missionIds.has(marker.missionId) ||
      seen.has(marker.missionId) ||
      typeof marker.x !== 'number' ||
      !Number.isFinite(marker.x) ||
      marker.x < 0 ||
      marker.x > 1 ||
      typeof marker.y !== 'number' ||
      !Number.isFinite(marker.y) ||
      marker.y < 0 ||
      marker.y > 1
    )
      throw new Error('Markøren har et ukendt id, en dublet eller en ugyldig position.');
    seen.add(marker.missionId);
    return { missionId: marker.missionId, x: marker.x, y: marker.y };
  });
  if (!seen.has(soldierMissionId)) throw new Error('Soldaterbøgen skal bevares på kortet.');
  return { version: 1, markers };
}

export function clampMapPosition(point: Point): Point {
  return {
    x: Math.round(Math.max(0.06, Math.min(0.94, point.x)) * 10000) / 10000,
    y: Math.round(Math.max(0.17, Math.min(0.85, point.y)) * 10000) / 10000,
  };
}

export function draggedMapPosition(
  start: Point,
  delta: Point,
  size: { width: number; height: number },
): Point {
  return clampMapPosition({
    x: start.x + delta.x / Math.max(1, size.width),
    y: start.y + delta.y / Math.max(1, size.height),
  });
}

export class MapLayoutDraft {
  #saved: MapLayout;
  #draft: MapLayout | null = null;

  constructor(layout: MapLayout) {
    this.#saved = parseMapLayout(layout);
  }

  get current(): MapLayout {
    return this.#draft ?? this.#saved;
  }
  get editing(): boolean {
    return this.#draft !== null;
  }

  begin(): void {
    this.#draft ??= structuredClone(this.#saved);
  }
  cancel(): void {
    this.#draft = null;
  }

  accept(layout: MapLayout): void {
    this.#saved = parseMapLayout(layout);
    this.#draft = null;
  }

  add(missionId: string): void {
    if (!missionIds.has(missionId)) return;
    this.begin();
    if (this.current.markers.some((marker) => marker.missionId === missionId)) return;
    // Nye opgaver placeres på en fri plads nær midten, så de straks kan trækkes.
    let position: Point = { x: 0.5, y: 0.5 };
    for (let i = 0; i < 90; i++) {
      const candidate = { x: 0.14 + (i % 9) * 0.09, y: 0.23 + Math.floor(i / 9) * 0.06 };
      if (i === 0) Object.assign(candidate, position);
      if (
        !this.current.markers.some((m) => Math.hypot(m.x - candidate.x, m.y - candidate.y) < 0.055)
      ) {
        position = candidate;
        break;
      }
    }
    this.current.markers.push({ missionId, ...position });
  }

  move(missionId: string, position: Point): void {
    if (!this.#draft) return;
    const marker = this.#draft.markers.find((entry) => entry.missionId === missionId);
    if (marker) Object.assign(marker, clampMapPosition(position));
  }

  remove(missionId: string): void {
    if (!this.#draft || missionId === soldierMissionId) return;
    this.#draft.markers = this.#draft.markers.filter((marker) => marker.missionId !== missionId);
  }
}
